import { Injectable } from '@angular/core';
import * as Blockly from 'blockly';
import { firstValueFrom } from 'rxjs';
import DEVICE_TOOLBOX from 'src/assets/constants/device/blockly-toolbox-device';
import { DEVICE_BLOCKS } from 'src/assets/constants/device/predefined-device';
import { MODEL_POINTS_BLOCKS, dynamicDeviceModelBlockBuilder } from 'src/assets/constants/device/model-points-blocks';
import { DeviceCodeGenerate } from 'src/assets/constants/device/predefined-code-generator-device';
import { BlockTheme, categoryTheme } from 'src/assets/constants/blockly-theme';
import { SiteSequencerService } from 'src/app/components/site-sequencer/services/site-sequencer.service';
import { BlockFieldName, BlockType, CustomCategory, DeviceRunBlockType } from 'src/app/shared/enums/SequenceKeys.enum';
import { CommonService } from 'src/app/services/common.service';

@Injectable({
    providedIn: 'root'
})
export class BlocklyWorkspaceBuilderService {
    private blocklyWorkSpace: any;
    private toolBox: any;
    private hiddenContainer: HTMLDivElement | null = null;
    private codeSnippet = '';
    private sequenceName = '';
    private siteName = '';
    private allModelsCollection: any[] = [];
    private modelOptions: any[] = [];
    private createdPointsCollection: any[] = [];
    private setterBlocksToInitialize: Array<{ id: string; selected_point_ref: string | null }> = [];

    constructor(
        private siteSequencerService: SiteSequencerService,
        private commonService: CommonService
    ) { }

    /**
     * Processes sequence payload using an internal hidden Blockly container.
     */
    async processSequenceInHiddenContainer(
        data: any,
        options: { keepWorkspace?: boolean } = {}
    ): Promise<{
        createdPointsCollection: any[];
        uniqueModelIds: Array<{ id: string; name: string }>;
        deviceType: string;
        workspaceXml: string;
        sequenceName: string;
        siteName: string;
        snippet: string;
    }> {
        const containerEl = this.ensureHiddenContainer();
        return this.processSequenceInContainer(containerEl, data, options);
    }

    /**
     * Builds Blockly state from sequence data in the provided container and returns derived output.
     */
    async processSequenceInContainer(
        containerEl: HTMLDivElement,
        data: any,
        options: { keepWorkspace?: boolean } = {}
    ): Promise<{
        createdPointsCollection: any[];
        uniqueModelIds: Array<{ id: string; name: string }>;
        deviceType: string;
        workspaceXml: string;
        sequenceName: string;
        siteName: string;
        snippet: string;
    }> {
        this.sequenceName = data?.sequenceData?.seqName || data?.selectedSequence?.name || '';
        this.siteName = data?.siteData?.siteName || '';
        this.codeSnippet = data?.sequenceData?.snippet || '';
        this.modelOptions = Array.isArray(data?.sequenceData?.metadata) ? data.sequenceData.metadata : [];
        this.allModelsCollection = [];
        this.createdPointsCollection = [];
        this.setterBlocksToInitialize = [];

        this.destroyWorkspace();
        this.initializeBlocklyWorkspace(containerEl);
        await this.loadSequenceIntoWorkspace(data?.sequenceData?.blocklyXml || '');

        const buildData = this.getBuildData();
        if (!options.keepWorkspace) {
            this.destroyWorkspace();
        }
        return buildData;
    }

    /**
     * Disposes workspace and removes the hidden host container if present.
     */
    destroy(): void {
        this.destroyWorkspace();

        if (this.hiddenContainer?.parentNode) {
            this.hiddenContainer.parentNode.removeChild(this.hiddenContainer);
        }

        this.hiddenContainer = null;
    }

    /**
     * Disposes the active Blockly workspace reference.
     */
    private destroyWorkspace(): void {
        this.blocklyWorkSpace?.dispose?.();
        this.blocklyWorkSpace = null;
    }

    /**
     * Ensures a detached visual host exists for hidden Blockly processing.
     */
    private ensureHiddenContainer(): HTMLDivElement {
        if (this.hiddenContainer && document.body.contains(this.hiddenContainer)) {
            return this.hiddenContainer;
        }

        const container = document.createElement('div');
        container.id = 'equipment-builder-hidden-blockly-host';
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '-9999px';
        container.style.width = '1px';
        container.style.height = '1px';
        container.style.overflow = 'hidden';
        container.style.opacity = '0';
        container.style.pointerEvents = 'none';

        document.body.appendChild(container);
        this.hiddenContainer = container;
        return container;
    }

    /**
     * Returns basic display state captured during sequence processing.
     */
    getViewState(): { sequenceName: string; siteName: string; codeSnippet: string } {
        return {
            sequenceName: this.sequenceName,
            siteName: this.siteName,
            codeSnippet: this.codeSnippet
        };
    }

    /**
     * Returns accumulated build data derived from the current workspace state.
     */
    getBuildData(): {
        createdPointsCollection: any[];
        uniqueModelIds: Array<{ id: string; name: string }>;
        deviceType: string;
        workspaceXml: string;
        sequenceName: string;
        siteName: string;
        snippet: string;
    } {
        return {
            createdPointsCollection: [...this.createdPointsCollection],
            uniqueModelIds: this.getUniqueModelIdsFromWorkspace(),
            deviceType: this.getRunStartupDeviceType() || 'CONNECT_MODULE',
            workspaceXml: this.getWorkspaceXml(),
            sequenceName: this.sequenceName,
            siteName: this.siteName,
            snippet: this.codeSnippet
        };
    }

    /**
     * Serializes the current Blockly workspace to XML text.
     */
    private getWorkspaceXml(): string {
        if (!this.blocklyWorkSpace) {
            return '';
        }

        const xml = Blockly.Xml.workspaceToDom(this.blocklyWorkSpace);
        return Blockly.Xml.domToText(xml);
    }

    /**
     * Collects distinct model ids and labels from model blocks in a workspace.
     */
    getUniqueModelIdsFromWorkspace(workspace: any = this.blocklyWorkSpace): Array<{ id: string; name: string }> {
        const allBlocks = workspace?.getAllBlocks(true) || [];
        const modelBlocks = allBlocks.filter((blockObj: any) => blockObj.type === BlockType.MODEL_BLOCK);
        const modelIds: Array<{ id: string; name: string }> = [];

        modelBlocks.forEach((block: any) => {
            const modelId = block.getFieldValue(BlockFieldName.SELECTED_MODEL);
            if (!modelId || modelId === 'DEFAULT_MODAL') {
                return;
            }

            const version = block.getFieldValue('modelVersion') || '';
            const field = block.getField(BlockFieldName.SELECTED_MODEL) || block.getField('selected_model');
            const selectedLabel = field?.selectedOption?.[0] || '';
            const modelName = String(selectedLabel)
                .replace(`(v ${version})`, '')
                .trim() || modelId;

            modelIds.push({
                id: modelId,
                name: modelName
            });
        });

        return modelIds.filter((value, index, self) =>
            index === self.findIndex((targetObj) => targetObj.id === value.id)
        );
    }

    /**
     * Reads selected device type from run-startup block, defaulting to CONNECT_MODULE.
     */
    getRunStartupDeviceType(workspace: any = this.blocklyWorkSpace): string | null {
        const allBlocks = workspace?.getAllBlocks(true) || [];
        const runStartupBlock = allBlocks.find((blockObj: any) => blockObj.type === DeviceRunBlockType.RUN_BLOCK_STARTUP);
        return runStartupBlock ? runStartupBlock.getFieldValue('DEVICE_TYPE') : 'CONNECT_MODULE';
    }

    /**
     * Checks if the run-startup device type is SMART_NODE.
     */
    isSmartNodeDeviceType(workspace: any = this.blocklyWorkSpace): boolean {
        return this.getRunStartupDeviceType(workspace) === 'SMART_NODE';
    }

    /**
     * Checks whether a run-startup block exists in the workspace.
     */
    hasDeviceRunStartupBlock(workspace: any = this.blocklyWorkSpace): boolean {
        const allBlocks = workspace?.getAllBlocks(true) || [];
        return allBlocks.some((blockObj: any) => blockObj.type === DeviceRunBlockType.RUN_BLOCK_STARTUP);
    }

    /**
     * Returns workspace blocks matching any of the requested block types.
     */
    getWorkspaceBlocksByTypes(workspace: any, blockTypes: string[] = []): any[] {
        if (!workspace || !blockTypes.length) {
            return [];
        }

        const supportedTypes = new Set(blockTypes);
        const allBlocks = workspace?.getAllBlocks(true) || [];
        return allBlocks.filter((blockObj: any) => supportedTypes.has(blockObj.type));
    }

    /**
     * Resolves block type from a Blockly event, including create/delete payload fallbacks.
     */
    getBlockTypeFromEvent(workspace: any, event: any): string {
        const targetBlock = workspace?.getBlockById?.(event?.blockId);
        const currentBlockType = event?.type === 'create'
            ? event?.json?.type
            : (event?.type === 'delete' ? event?.oldJson?.type : '');
        return targetBlock?.type || currentBlockType || '';
    }

    /**
     * Identifies Blockly procedure definition blocks.
     */
    isProcedureDefinitionBlock(block: any): boolean {
        return block?.type === 'procedures_defnoreturn' || block?.type === 'procedures_defreturn';
    }

    /**
     * Checks whether any procedure call blocks are present.
     */
    hasProcedureCallsInBlocks(blocks: any[] = []): boolean {
        return (blocks || []).some((block: any) => block?.type === 'procedures_callnoreturn' || block?.type === 'procedures_callreturn');
    }

    /**
     * Returns normalized procedure call name from a procedure-call block.
     */
    getProcedureCallName(block: any): string {
        if (!block) {
            return '';
        }

        const procedureCall = block.getProcedureCall?.();
        if (typeof procedureCall === 'string' && procedureCall.trim()) {
            return procedureCall.trim();
        }

        return (block.getFieldValue?.('NAME') || '').trim();
    }

    /**
     * Returns procedure definition block name.
     */
    getProcedureBlockName(block: any): string {
        return (block?.getFieldValue?.('NAME') || '').trim();
    }

    /**
     * Collects distinct called procedure names from a block list.
     */
    getCalledProcedureNamesFromBlocks(blocks: any[] = []): string[] {
        const names = (blocks || [])
            .filter((block: any) => block?.type === 'procedures_callnoreturn' || block?.type === 'procedures_callreturn')
            .map((block: any) => this.getProcedureCallName(block))
            .filter((name: string) => !!name);

        return Array.from(new Set(names));
    }

    /**
     * Registers block definitions when non-empty JSON definitions are provided.
     */
    registerBlockDefinitions(blocksToInit: any[] = []): void {
        if (!Array.isArray(blocksToInit) || !blocksToInit.length) {
            return;
        }

        Blockly.defineBlocksWithJsonArray(blocksToInit as any);
    }

    /**
     * Injects and themes a Blockly workspace for the given container and toolbox.
     */
    createWorkspace(blocklyContainer: string | HTMLElement, toolbox: any, themeName = 'customTheme'): any {
        const workspace = Blockly.inject(blocklyContainer as any, {
            toolbox,
            grid: {
                spacing: 10,
                length: 2,
                snap: true,
                colour: '#ccc'
            },
            zoom: {
                controls: true,
                startScale: 0.8,
                maxScale: 3,
                minScale: 0.3,
                scaleSpeed: 1.2,
                pinch: true,
                wheel: true
            },
            move: {
                drag: true,
                wheel: true
            },
            trashcan: true,
        });

        const theme = Blockly.Theme.defineTheme(themeName, {
            blockStyles: BlockTheme.blockStyles,
            categoryStyles: categoryTheme.categoryStyle,
            name: ''
        });

        workspace.setTheme(theme);
        return workspace;
    }

    /**
     * Creates a Blockly workspace only after the target container is attached to the DOM.
     * Retries for transient render timing issues and returns null on exhaustion.
     */
    async createWorkspaceWithRetry(config: {
        existingWorkspace?: any;
        selectedSite?: any;
        blocklyContainerId: string;
        toolbox: any;
        themeName?: string;
        retryLimit?: number;
        retryDelayMs?: number;
    }): Promise<any> {
        const {
            existingWorkspace,
            selectedSite,
            blocklyContainerId,
            toolbox,
            themeName = 'customTheme',
            retryLimit = 20,
            retryDelayMs = 50
        } = config;

        if (existingWorkspace) {
            return existingWorkspace;
        }

        if (!selectedSite) {
            return null;
        }

        for (let attempt = 0; attempt <= retryLimit; attempt++) {
            const blocklyContainer = document.getElementById(blocklyContainerId);
            const isContainerReady = !!blocklyContainer && document.contains(blocklyContainer);

            if (isContainerReady) {
                try {
                    return this.createWorkspace(blocklyContainerId, toolbox, themeName);
                } catch {
                    // Retry until retryLimit, then fail gracefully.
                }
            }

            if (attempt < retryLimit) {
                await this.wait(retryDelayMs);
            }
        }

        return null;
    }

    /**
     * Delays execution for the specified duration in milliseconds.
     */
    private wait(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Normalizes partial Blockly XML into a valid workspace XML envelope.
     */
    normalizeWorkspaceXml(workspaceXml: any): string {
        const xmlStart = '<xml xmlns="https://developers.google.com/blockly/xml">';
        const xmlEnd = '</xml>';
        const trimmedXml = typeof workspaceXml === 'string' ? workspaceXml.trim() : '';
        const wordToReplace = '<block xmlns="https://developers.google.com/blockly/xml"';
        const replacementWord = '<block';
        const modifiedString = trimmedXml.replace(new RegExp(wordToReplace, 'g'), replacementWord);

        if (modifiedString.startsWith('<xml')) {
            return modifiedString;
        }

        return `${xmlStart}${modifiedString}${xmlEnd}`;
    }

    /**
     * Applies generated parameter values to point_setter blocks inside sequence_run_startup blocks.
     * Generated parameter names are matched against metadata domainName entries.
     */
    applyGeneratedValuesToStartupSetters(
        sequenceReferenceData: any,
        generatedParameterDetails: Array<{ name: string; value: any }>
    ): any {
        const blocklyXml = String(sequenceReferenceData?.blocklyXml || '').trim();
        const metadata = Array.isArray(sequenceReferenceData?.metadata) ? sequenceReferenceData.metadata : [];

        if (!blocklyXml || !Array.isArray(generatedParameterDetails) || !generatedParameterDetails.length || !metadata.length) {
            return sequenceReferenceData;
        }

        const normalize = (value: any): string => String(value ?? '').trim().toLowerCase();
        const normalizePointName = (value: any): string => normalize(value).replace(/[^a-z0-9]+/g, '');
        const toDomainKey = (value: any): string => {
            const normalized = normalize(value);
            return normalized.startsWith('model_point_') ? normalized.slice('model_point_'.length) : normalized;
        };

        const valueByDomain = new Map<string, any>();
        generatedParameterDetails.forEach((detail) => {
            const domainKey = toDomainKey(detail?.name);
            if (!domainKey) {
                return;
            }
            valueByDomain.set(domainKey, detail?.value);
        });

        if (!valueByDomain.size) {
            return sequenceReferenceData;
        }

        const mappedPoints: Array<{ modelId: string; domainName: string; pointName: string; registerAddress: string; value: any }> = [];

        metadata.forEach((model: any) => {
            const modelId = String(model?.modelId || '').trim();
            const points = Array.isArray(model?.points) ? model.points : [];

            points.forEach((point: any) => {
                const domainName = String(point?.domainName || '').trim();
                const domainKey = normalize(domainName);

                if (!domainKey || !valueByDomain.has(domainKey)) {
                    return;
                }

                mappedPoints.push({
                    modelId,
                    domainName,
                    pointName: String(point?.pointName || '').trim(),
                    registerAddress: String(point?.registerAddress || '').trim(),
                    value: valueByDomain.get(domainKey)
                });
            });
        });

        if (!mappedPoints.length) {
            return sequenceReferenceData;
        }

        const parser = new DOMParser();
        const xmlDocument = parser.parseFromString(blocklyXml, 'text/xml');
        if (xmlDocument.getElementsByTagName('parsererror').length > 0) {
            return sequenceReferenceData;
        }

        const pointsByModelAndDomain = new Map<string, { modelId: string; domainName: string; pointName: string; registerAddress: string; value: any }>();
        mappedPoints.forEach((point) => {
            pointsByModelAndDomain.set(`${point.modelId}::${normalize(point.domainName)}`, point);
        });

        const startupBlocks = Array.from(xmlDocument.querySelectorAll('block[type="sequence_run_startup"]'));

        startupBlocks.forEach((startupBlock) => {
            const setterBlocks = Array.from(startupBlock.querySelectorAll('block[type="point_setter"]'));

            setterBlocks.forEach((setterBlock) => {
                const selectedPointNameField = setterBlock.querySelector('field[name="selected_point_name"]');
                const selectedModelIdField = setterBlock.querySelector('field[name="setter_model_value"]');

                if (!selectedPointNameField) {
                    return;
                }

                const selectedPointNameRaw = String(selectedPointNameField.textContent || '').trim();
                const selectedModelIdRaw = String(selectedModelIdField?.textContent || '').trim();
                const strippedPointName = selectedPointNameRaw.replace(/^model_point_/i, '');
                const candidateDomainNames = [
                    normalize(strippedPointName),
                    normalize(strippedPointName.replace(/_\d+$/, '')),
                    normalize(strippedPointName.split('#')[0] || strippedPointName)
                ].filter(Boolean);

                let mappedPoint: any = null;
                for (const candidateDomainName of candidateDomainNames) {
                    mappedPoint = pointsByModelAndDomain.get(`${selectedModelIdRaw}::${candidateDomainName}`);
                    if (mappedPoint) {
                        break;
                    }
                }

                if (!mappedPoint) {
                    const candidatePoints = mappedPoints.filter((point) => !selectedModelIdRaw || point.modelId === selectedModelIdRaw);

                    const selectedPointLabel = normalize(strippedPointName.replace(/#\d+$/, ''));
                    const selectedPointKey = normalizePointName(selectedPointLabel);

                    // Prioritize exact label and register-address aware keys to avoid accidental
                    // matches between similarly named points like "testing" and "testing 1".
                    const exactNameMatch = candidatePoints.find((point) => normalize(point.pointName) === selectedPointLabel);
                    const exactNameWithAddressMatch = candidatePoints.find((point) => {
                        if (!point.registerAddress) {
                            return false;
                        }

                        const pointWithAddressLabel = normalize(`${point.pointName}_${point.registerAddress}`);
                        const pointWithAddressKey = normalizePointName(pointWithAddressLabel);
                        return selectedPointKey === pointWithAddressKey;
                    });
                    const exactNameKeyMatch = candidatePoints.find((point) => normalizePointName(point.pointName) === selectedPointKey);

                    mappedPoint = exactNameMatch || exactNameWithAddressMatch || exactNameKeyMatch || null;
                }

                if (!mappedPoint) {
                    return;
                }

                const valueField = setterBlock.querySelector('value[name="input_value_field"] > block[type="math_number"] > field[name="NUM"]')
                    || setterBlock.querySelector('value[name="input_value_field"] field[name="NUM"]');

                if (!valueField) {
                    return;
                }

                valueField.textContent = String(mappedPoint.value);
            });
        });

        return {
            ...sequenceReferenceData,
            blocklyXml: new XMLSerializer().serializeToString(xmlDocument)
        };
    }

    /**
     * Clears and loads XML into an existing workspace, optionally marking blocks as created.
     */
    async loadXmlIntoWorkspace(workspace: any, workspaceXml: any, markBlocksAsCreated = true): Promise<any[]> {
        if (!workspace) {
            return [];
        }

        const parser = new DOMParser();
        const formattedWorkspaceXml = this.normalizeWorkspaceXml(workspaceXml);

        if (!formattedWorkspaceXml) {
            workspace.clear?.();
            return [];
        }

        const xmlDoc: any = parser.parseFromString(formattedWorkspaceXml, 'text/xml');

        // Fail-safe for malformed XML: avoid mutating current workspace with invalid payloads.
        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
            return [];
        }

        workspace.clear?.();
        const loadedBlocks = await Blockly.Xml.domToWorkspace(xmlDoc.documentElement, workspace);
        const allBlocks = workspace.getAllBlocks?.(true) || [];

        if (markBlocksAsCreated) {
            allBlocks.forEach((blockObj: any) => blockObj['isCreated'] = true);
        }

        return loadedBlocks;
    }

    /**
     * Parses workspace XML and returns model-point and setter references used for post-load hydration.
     */
    extractPointAndSetterBlocksFromXml(workspaceXml: any): {
        pointBlocksToInitialize: Array<{ id: string | null; type: string }>;
        setterBlocksToInitialize: Array<{ id: string; selected_point_ref: string | null }>;
    } {
        const formattedWorkspaceXml = this.normalizeWorkspaceXml(workspaceXml);

        if (!formattedWorkspaceXml) {
            return {
                pointBlocksToInitialize: [],
                setterBlocksToInitialize: []
            };
        }

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(formattedWorkspaceXml, 'text/xml');

        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
            return {
                pointBlocksToInitialize: [],
                setterBlocksToInitialize: []
            };
        }

        const pointBlocksToInitialize: Array<{ id: string | null; type: string }> = [];
        const setterBlocksToInitialize: Array<{ id: string; selected_point_ref: string | null }> = [];
        const blocks = Array.from(xmlDoc.getElementsByTagName('block'));

        blocks.forEach((block: any) => {
            const type = block.getAttribute('type');
            if (!type) {
                return;
            }

            if (type.includes(BlockType.MODEL_POINT)) {
                pointBlocksToInitialize.push({
                    id: block.getAttribute('id'),
                    type
                });
                return;
            }

            if (type !== BlockType.POINT_SETTER) {
                return;
            }

            const blockId = block.getAttribute('id');
            if (!blockId) {
                return;
            }

            const fields = Array.from(block.getElementsByTagName('field'));
            const selectedSetterField = fields.find((field: any) => field.getAttribute('name') === 'selected_point_ref') as any;

            setterBlocksToInitialize.push({
                id: blockId,
                selected_point_ref: selectedSetterField ? selectedSetterField.textContent : null
            });
        });

        return {
            pointBlocksToInitialize,
            setterBlocksToInitialize
        };
    }

    /**
     * Injects a temporary workspace into a container and loads XML into it.
     */
    async loadXmlIntoContainer(containerEl: HTMLDivElement, workspaceXml: any, injectConfig: any, markBlocksAsCreated = true): Promise<{ workspace: any; loadedBlocks: any[] }> {
        const workspace = Blockly.inject(containerEl, injectConfig);
        const loadedBlocks = await this.loadXmlIntoWorkspace(workspace, workspaceXml, markBlocksAsCreated);
        return { workspace, loadedBlocks };
    }

    /**
     * Initializes service-owned Blockly workspace with device blocks, generators, and theme.
     */
    private initializeBlocklyWorkspace(containerEl: HTMLDivElement): void {
        const blocksToInit = [...DEVICE_BLOCKS, ...MODEL_POINTS_BLOCKS];
        Blockly.defineBlocksWithJsonArray(blocksToInit as any);
        DeviceCodeGenerate.addCodeGenerators();

        this.toolBox = JSON.parse(JSON.stringify(DEVICE_TOOLBOX));

        this.blocklyWorkSpace = Blockly.inject(containerEl, {
            toolbox: this.toolBox,
            grid: {
                spacing: 10,
                length: 2,
                snap: true,
                colour: '#ccc'
            },
            zoom: {
                controls: true,
                startScale: 0.8,
                maxScale: 3,
                minScale: 0.3,
                scaleSpeed: 1.2,
                pinch: true,
                wheel: true
            },
            move: {
                drag: true,
                wheel: true
            },
            trashcan: true
        });

        const theme = Blockly.Theme.defineTheme('equipmentBuilderDeviceTheme', {
            blockStyles: BlockTheme.blockStyles,
            categoryStyles: categoryTheme.categoryStyle,
            name: ''
        });

        this.blocklyWorkSpace.setTheme(theme);
    }

    /**
     * Loads sequence XML and performs post-load model/point hydration workflow.
     */
    private async loadSequenceIntoWorkspace(workspaceXml: string): Promise<void> {
        const formattedWorkspaceXml = this.normalizeWorkspaceXml(workspaceXml);

        if (!formattedWorkspaceXml) {
            return;
        }

        const parser = new DOMParser();
        const xmlDoc: any = parser.parseFromString(formattedWorkspaceXml, 'text/xml');

        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
            return;
        }

        this.extractPointBlocksFromXml(formattedWorkspaceXml);
        this.registerDynamicModelPointBlocks(xmlDoc);

        this.blocklyWorkSpace.clear();
        Blockly.Xml.domToWorkspace(xmlDoc.documentElement, this.blocklyWorkSpace);
        this.handleLoadedModelAndSetterBlocks();
        this.subscribeDropdownClicksForExistingBlocks();
        this.blocklyWorkSpace.scrollCenter();

        await this.postLoadSyncForPointsAndSetters();
    }

    /**
     * Extracts setter block references used to restore point selections after load.
     */
    private extractPointBlocksFromXml(workspaceXml: string): void {
        const extractedBlocks = this.extractPointAndSetterBlocksFromXml(workspaceXml);
        this.setterBlocksToInitialize = extractedBlocks.setterBlocksToInitialize;
    }

    /**
     * Applies model dropdown state and hides internal setter fields on loaded blocks.
     */
    private handleLoadedModelAndSetterBlocks(): void {
        const allBlocks = this.blocklyWorkSpace?.getAllBlocks(true) || [];

        allBlocks.forEach((blockObj: any) => {
            if (blockObj.type === BlockType.MODEL_BLOCK) {
                this.setModelBlockDropdownOptions(blockObj);
            }

            if (blockObj.type === BlockType.POINT_SETTER) {
                blockObj.getField('selected_point_ref')?.setVisible(false);
                blockObj.getField('setter_model_value')?.setVisible(false);
            }
        });

        this.blocklyWorkSpace?.render?.();
    }

    /**
     * Subscribes dropdown click handlers for already-loaded model and setter blocks.
     */
    private subscribeDropdownClicksForExistingBlocks(): void {
        const allBlocks = this.blocklyWorkSpace?.getAllBlocks(true) || [];

        allBlocks.forEach((blockObj: any) => {
            if (blockObj.type === BlockType.MODEL_BLOCK) {
                this.commonService.subcribeToScopeDropdownClick(blockObj, BlockFieldName.SELECTED_MODEL, true);
            }

            if (blockObj.type === BlockType.POINT_SETTER) {
                this.commonService.subcribeToScopeDropdownClick(blockObj, BlockFieldName.SELECTED_POINT_NAME, true);
            }
        });
    }

    /**
     * Runs post-load point construction and setter restoration.
     */
    private async postLoadSyncForPointsAndSetters(): Promise<void> {
        await this.fetchPointsForSelectedModals();
        this.initializeSetterBlocksFromSavedRefs();
    }

    /**
     * Restores setter dropdown selections using saved point references.
     */
    private initializeSetterBlocksFromSavedRefs(): void {
        const defaultOption = { label: 'select point', value: 'DEFAULT_MODAL' };

        this.setterBlocksToInitialize.forEach((setterConfig: any) => {
            const setterBlock = this.blocklyWorkSpace?.getBlockById?.(setterConfig.id);
            if (!setterBlock || setterBlock.type !== BlockType.POINT_SETTER) {
                return;
            }

            const targetPoint = this.createdPointsCollection.find((pointObj: any) => pointObj.type === setterConfig.selected_point_ref);

            if (targetPoint) {
                const targetOption = [{ label: targetPoint.label, value: targetPoint.type }];
                this.commonService.setDropdownOptionsForBlock(
                    setterBlock,
                    this.blocklyWorkSpace,
                    BlockFieldName.SELECTED_POINT_NAME,
                    targetOption,
                    false
                );
                setterBlock.setFieldValue(targetPoint.type, BlockFieldName.SELECTED_POINT_NAME);
                setterBlock.setFieldValue(targetPoint.type, 'selected_point_ref');
                setterBlock.setFieldValue(targetPoint.modalId || '', 'setter_model_value');
            } else {
                this.commonService.setDropdownOptionsForBlock(
                    setterBlock,
                    this.blocklyWorkSpace,
                    BlockFieldName.SELECTED_POINT_NAME,
                    [defaultOption],
                    false
                );
                setterBlock.setFieldValue(defaultOption.value, BlockFieldName.SELECTED_POINT_NAME);
                setterBlock.setFieldValue(defaultOption.value, 'selected_point_ref');
                setterBlock.setFieldValue('', 'setter_model_value');
            }

            setterBlock.getField('selected_point_ref')?.setVisible(false);
            setterBlock.getField('setter_model_value')?.setVisible(false);
        });

        this.blocklyWorkSpace?.render?.();
    }

    /**
     * Binds model dropdown options for a model block from metadata when available.
     */
    private setModelBlockDropdownOptions(targetBlock: any): void {
        const existingSelectedModelId = targetBlock.getFieldValue('selectedModelId');
        const modelVersion = targetBlock.getFieldValue('modelVersion');
        const selectedModelConfig = this.getModelConfigFromMetadata(existingSelectedModelId, modelVersion);

        const targetOption = {
            label: 'select model',
            value: 'DEFAULT_MODAL'
        };

        if (existingSelectedModelId && selectedModelConfig) {
            targetOption.label = this.commonService.getModelNameWithVersion(selectedModelConfig);
            targetOption.value = selectedModelConfig.modelId;
            this.commonService.setDropdownOptionsForBlock(
                targetBlock,
                this.blocklyWorkSpace,
                BlockFieldName.SELECTED_MODEL,
                [targetOption],
                true
            );
            targetBlock.setFieldValue(selectedModelConfig.modelId, BlockFieldName.SELECTED_MODEL);
            targetBlock.setFieldValue(selectedModelConfig.modelId, 'selectedModelId');
            targetBlock.setFieldValue(selectedModelConfig.modelVersion || '', 'modelVersion');
            targetBlock['isReferencingMetadata'] = true;
        }

        targetBlock.getField('selectedModelId')?.setVisible(false);
        targetBlock.getField('modelVersion')?.setVisible(false);
    }

    /**
     * Finds model metadata entry by model id and optional version.
     */
    private getModelConfigFromMetadata(selectedModelId: string, modelVersion?: string | null): any {
        if (!selectedModelId) {
            return null;
        }

        const matchingById = this.modelOptions.filter((option: any) =>
            option?.modelId?.includes?.(selectedModelId)
        );

        if (!matchingById.length) {
            return null;
        }

        if (modelVersion) {
            const versionMatched = matchingById.find((option: any) => String(option?.modelVersion || '') === String(modelVersion));
            if (versionMatched) {
                return versionMatched;
            }
        }

        return matchingById[0];
    }

    /**
     * Builds model-point block descriptors for selected models using metadata and API data.
     */
    private async fetchPointsForSelectedModals(): Promise<any[]> {
        const modelBlocks = (this.blocklyWorkSpace?.getAllBlocks(true) || []).filter(
            (blockObj: any) => blockObj.type === BlockType.MODEL_BLOCK
        );

        if (!modelBlocks.length) {
            this.createdPointsCollection = [];
            this.updateModelPointsToolbox([]);
            return [];
        }

        const selectedModelBlocks = modelBlocks.filter((blockObj: any) => {
            const selectedModel = (blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL) || '').trim();
            return selectedModel && selectedModel !== 'DEFAULT_MODAL';
        });

        if (!selectedModelBlocks.length) {
            this.createdPointsCollection = [];
            this.updateModelPointsToolbox([]);
            return [];
        }

        const blocksUsingModelOptions = selectedModelBlocks.filter((blockObj: any) => blockObj['isReferencingMetadata'] === true);
        const blocksUsingAPI = selectedModelBlocks.filter((blockObj: any) => !blockObj['isReferencingMetadata']);

        const sourceModelsById = new Map<string, any>();

        blocksUsingModelOptions.forEach((blockObj: any) => {
            const selectedModelId = blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL);
            const selectedModelVersion = blockObj.getFieldValue('modelVersion') || null;
            const modelFromOptions = this.getModelConfigFromMetadata(selectedModelId, selectedModelVersion);

            if (modelFromOptions?.points?.length) {
                sourceModelsById.set(selectedModelId, {
                    id: selectedModelId,
                    name: modelFromOptions.modelName || selectedModelId,
                    version: modelFromOptions.modelVersion || selectedModelVersion,
                    points: (modelFromOptions.points || []).map((pointObj: any) => ({
                        pointId: pointObj.pointId,
                        name: pointObj.pointName,
                        domainName: pointObj.domainName ?? pointObj.pointName,
                        defaultValue: pointObj.defaultValue ?? null,
                        registerAddress: pointObj.registerAddress ?? null
                    }))
                });
            }
        });

        const idsForAPI = Array.from(new Set(
            [...blocksUsingAPI, ...blocksUsingModelOptions]
                .map((blockObj: any) => blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL))
                .filter((id: string) => !!id && !sourceModelsById.has(id))
        ));

        if (idsForAPI.length > 0) {
            try {
                const apiResponse: any = await firstValueFrom(this.siteSequencerService.getModelPoints(idsForAPI));
                const apiModels = Array.isArray(apiResponse) ? apiResponse : [];
                apiModels.forEach((modelObj: any) => {
                    if (!modelObj?.id) {
                        return;
                    }

                    const modelFromCollection = this.allModelsCollection.find((collectionModel: any) => collectionModel.value === modelObj.id);

                    sourceModelsById.set(modelObj.id, {
                        id: modelObj.id,
                        name: modelObj.name || modelFromCollection?.label || modelObj.id,
                        version: modelFromCollection?.version || null,
                        points: modelObj.points || []
                    });
                });
            } catch {
                this.commonService.showToast('error', 'Failed to fetch points for selected model(s).');
            }
        }

        const domainUsageCount: Record<string, number> = {};
        const modelUsageCount: Record<string, number> = {};
        const pointsToCreate: any[] = [];

        selectedModelBlocks.forEach((blockObj: any) => {
            const selectedModelId = blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL);
            const modelVersion = blockObj.getFieldValue('modelVersion') || sourceModelsById.get(selectedModelId)?.version || null;
            const modelConfig = sourceModelsById.get(selectedModelId);

            if (!modelConfig || !Array.isArray(modelConfig.points) || !modelConfig.points.length) {
                return;
            }

            const usageIndex = modelUsageCount[selectedModelId] ?? 0;
            modelUsageCount[selectedModelId] = usageIndex + 1;

            modelConfig.points.forEach((pointObj: any) => {
                const domainName = pointObj.domainName || pointObj.name || '';
                const duplicateIndex = domainUsageCount[domainName] ?? 0;
                domainUsageCount[domainName] = duplicateIndex + 1;
                const uniqueDomainName = duplicateIndex === 0 ? domainName : `${domainName}#${duplicateIndex}`;
                const suffix = usageIndex > 0 ? `_${usageIndex}` : '';

                pointsToCreate.push({
                    modalName: modelConfig.name,
                    modalId: usageIndex > 0 ? `${selectedModelId}_${usageIndex}` : selectedModelId,
                    modelVersion,
                    name: pointObj.name,
                    id: pointObj.pointId,
                    domain: uniqueDomainName,
                    label: `${modelConfig.name}_${pointObj.name}${suffix}`,
                    type: `${BlockType.MODEL_POINT}_${uniqueDomainName}${suffix}`,
                    defaultValue: pointObj.defaultValue ?? null,
                    registerAddress: pointObj.registerAddress ?? null
                });
            });
        });

        this.createdPointsCollection = this.assignPortsToModelPoints(pointsToCreate);
        this.updateModelPointsToolbox(this.createdPointsCollection);

        return this.createdPointsCollection.map((pointObj: any) => ({
            value: pointObj.type,
            label: pointObj.label
        }));
    }

    /**
     * Assigns default ports for model points based on model block order.
     * Uses the same base logic as Site Sequencer: start at 40000 and increment by 2.
     */
    assignPortsToModelPointsByWorkspaceOrder(workspace: any, modelPoints: any[]): any[] {
        const modelBlocksWithModel = workspace?.getAllBlocks(true)
            .filter((blockObj: any) =>
                blockObj.type === BlockType.MODEL_BLOCK &&
                blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL) &&
                blockObj.getFieldValue(BlockFieldName.SELECTED_MODEL) !== 'DEFAULT_MODAL'
            ) || [];

        const sortedModelBlocksWithModels = modelBlocksWithModel.sort(
            (obj1: any, obj2: any) => obj1.getRelativeToSurfaceXY().y - obj2.getRelativeToSurfaceXY().y
        );

        let portCounter = 40000;
        const modelOccurrenceMap: Record<string, number> = {};
        const updatedModelPoints: any[] = [];

        sortedModelBlocksWithModels.forEach((modelBlock: any) => {
            let selectedModelId = modelBlock.getFieldValue(BlockFieldName.SELECTED_MODEL);
            const modelVersion = modelBlock.getFieldValue('modelVersion') ?? null;
            const originalModelId = selectedModelId.split('_')[0];

            modelOccurrenceMap[originalModelId] = modelOccurrenceMap[originalModelId] ?? 0;
            selectedModelId = modelOccurrenceMap[originalModelId] > 0
                ? selectedModelId + `_${modelOccurrenceMap[selectedModelId]}`
                : selectedModelId;
            modelOccurrenceMap[originalModelId] = modelOccurrenceMap[originalModelId] + 1;

            const matchedModelPoints = modelPoints.filter((pointObj: any) =>
                pointObj.modalId === selectedModelId && pointObj.modelVersion === modelVersion
            );

            matchedModelPoints.forEach((pointObj: any) => {
                const registerAddress = Number(pointObj.registerAddress);
                const hasValidRegisterAddress = Number.isFinite(registerAddress);
                const resolvedPort = hasValidRegisterAddress ? registerAddress : portCounter;

                pointObj.port = resolvedPort;
                pointObj.label = `${pointObj.label}_${pointObj.port}`;

                // Use metadata registerAddress whenever available; fallback preserves old auto-increment behavior.
                if (hasValidRegisterAddress) {
                    portCounter = Math.max(portCounter, registerAddress + 2);
                } else {
                    portCounter = portCounter + 2;
                }
            });

            updatedModelPoints.push(...matchedModelPoints);
        });

        return updatedModelPoints;
    }

    // Assigns ports to model points based on their order in the workspace.
    private assignPortsToModelPoints(modelPoints: any[]): any[] {
        return this.assignPortsToModelPointsByWorkspaceOrder(this.blocklyWorkSpace, modelPoints);
    }

    // Updates the model points in the toolbox based on the provided block configuration array.
    private updateModelPointsToolbox(blockConfigArray: any[]): void {
        if (!this.toolBox?.contents?.length || !this.blocklyWorkSpace?.updateToolbox) {
            return;
        }

        const targetCategoryIndex = this.toolBox.contents.findIndex(
            (category: any) => category.name === CustomCategory.MODEL_POINTS
        );

        if (targetCategoryIndex < 0 || !this.toolBox.contents[targetCategoryIndex]?.contents) {
            return;
        }

        const existingContents = this.toolBox.contents[targetCategoryIndex].contents || [];
        const staticContents = existingContents.filter((blockObj: any) =>
            !String(blockObj?.type || '').includes(BlockType.MODEL_POINT)
        );

        const dynamicContents: any[] = [];
        blockConfigArray.forEach((blockConfig: any) => {
            dynamicDeviceModelBlockBuilder.modelPointBlockInit(blockConfig);
            dynamicContents.push({
                kind: 'block',
                type: blockConfig.type,
                enabled: true,
                name: blockConfig.label,
                defaultValue: blockConfig.defaultValue ?? null
            });
        });

        this.toolBox.contents[targetCategoryIndex].contents = [
            ...staticContents,
            ...dynamicContents
        ];

        this.blocklyWorkSpace.updateToolbox(this.toolBox);
    }

    /**
     * Registers dynamic model-point block types discovered in incoming XML.
     */
    private registerDynamicModelPointBlocks(xmlDoc: any): void {
        const blocks = Array.from(xmlDoc.getElementsByTagName('block'));
        const dynamicPointTypes = Array.from(new Set(
            blocks
                .map((block: any) => block.getAttribute('type') || '')
                .filter((type: string) => type.startsWith('model_point_'))
        ));

        dynamicPointTypes.forEach((type: string) => {
            if (!Blockly.Blocks[type]) {
                dynamicDeviceModelBlockBuilder.modelPointBlockInit({
                    type,
                    label: type,
                    id: type,
                    defaultValue: null
                });
            }
        });
    }
}